import React from 'react';

import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import Divider from 'legacy/common/components/wysiwyg-form/Divider';
import { AssociationMemberDto } from 'legacy/groups/types';
import {
  useRemoveAssociationMember,
  useUpdateMemberStatus
} from 'legacy/groups/hooks';
import * as toast from 'legacy/common/utils/toast';
import { useTranslation } from 'react-i18next';

type MoreOptionsProps = {
  associationId: string;
  closeMemberOptionsModal: () => void;
  visible: boolean;
  selectedMember: AssociationMemberDto | null;
};

const MemberOptionsModal: React.FC<MoreOptionsProps> = ({
  associationId,
  closeMemberOptionsModal,
  visible,
  selectedMember
}) => {
  const { mutate: updateMemberStatus } = useUpdateMemberStatus();
  const { mutate: removeMember } = useRemoveAssociationMember();
  const { t } = useTranslation(['associations']);

  const removeFromAssociation = () => {
    removeMember(
      {
        associationId,
        userId: selectedMember.user.id
      },
      {
        onSuccess: () => {
          toast.success(
            `${selectedMember.user.firstName} ${selectedMember.user.lastName} has been removed from the association.`
          );
          closeMemberOptionsModal();
        },
        onError: () => {
          toast.error(
            'An error occurred while trying to remove this user from the association.'
          );
        }
      }
    );
  };

  const makeAdmin = () => {
    updateMemberStatus(
      {
        associationId,
        userId: selectedMember.user.id,
        role: 'admin',
        status: 'approved'
      },
      {
        onSuccess: () => {
          toast.success(
            `${selectedMember.user.firstName} ${selectedMember.user.lastName} is now an admin of the association`
          );
          closeMemberOptionsModal();
        },
        onError: () => {
          toast.error(
            'An error occurred while trying to promote this user to admin.'
          );
        }
      }
    );
  };

  const removeAsAdmin = () => {
    updateMemberStatus(
      {
        associationId,
        userId: selectedMember.user.id,
        role: 'member',
        status: 'approved'
      },
      {
        onSuccess: () => {
          toast.success(
            `${selectedMember.user.firstName} ${selectedMember.user.lastName} is now a member of the association`
          );
          closeMemberOptionsModal();
        },
        onError: () => {
          toast.error(
            'An error occurred while trying to make this user a member.'
          );
        }
      }
    );
  };

  return (
    <Modal
      type={ModalType.SMALL}
      onClose={closeMemberOptionsModal}
      autoHeight
      hideCloseButton
      visible={visible}
    >
      <div className="p-4 flex flex-col">
        {selectedMember && (
          <div className="flex flex-col gap-4">
            <Button
              type={ButtonType.Danger}
              newRadius
              onClick={removeFromAssociation}
            >
              {t('members.actions.removeFromAssociation')}
            </Button>
            {selectedMember?.role === 'admin' && (
              <Button
                type={ButtonType.Danger}
                newRadius
                onClick={removeAsAdmin}
              >
                {t('members.actions.removeAsAdmin')}
              </Button>
            )}
            {selectedMember?.role === 'member' && (
              <Button type={ButtonType.Inverted} newRadius onClick={makeAdmin}>
                {t('members.actions.makeAdmin')}
              </Button>
            )}
          </div>
        )}
        <Divider size={4} />
        <div className="flex gap-2 flex-col pb-2 mt-6">
          <Button type={ButtonType.Inverted} onClick={closeMemberOptionsModal}>
            {t('members.actions.cancel')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default MemberOptionsModal;
